/**
 * WorkspaceBadge — header workspace switcher.
 *
 * Phase 15: dropdown showing all orgs the user belongs to. Click an entry
 * to switch active workspace; the project store re-bootstraps with that
 * org's data.
 *
 * Visibility:
 *  - Hidden if Supabase is not configured (parallel mode)
 *  - Hidden if user is not authenticated
 *  - Hidden until orgs have loaded (avoids flash of placeholder)
 *
 * State:
 *  - The list of orgs is fetched once when the user changes.
 *  - The active org id comes from the project store (which persists it
 *    to localStorage and uses it on every API call).
 *  - The 'projexhq:org-renamed' event from SettingsPage triggers a refetch
 *    so the badge reflects the new name immediately.
 */

import { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronsUpDown, Settings as SettingsIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { useProjectStore } from '@/store/useProjectStore';
import { isSupabaseConfigured } from '@/lib/supabase';
import { listMyOrgs, type CurrentOrg } from '@/lib/api';

export function WorkspaceBadge() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useProjectStore((s) => s.activeOrgId);
  const switchOrg = useProjectStore((s) => s.switchOrg);

  const [orgs, setOrgs] = useState<CurrentOrg[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load list of orgs (and re-load on rename event)
  useEffect(() => {
    if (!isSupabaseConfigured || !user) {
      setOrgs([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const res = await listMyOrgs();
      if (cancelled) return;
      if (res.data) setOrgs(res.data);
    };
    load();
    const handler = () => load();
    window.addEventListener('projexhq:org-renamed', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('projexhq:org-renamed', handler);
    };
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isSupabaseConfigured || !user || orgs.length === 0) return null;

  const activeOrg = orgs.find((o) => o.id === activeOrgId) ?? orgs[0];

  const handleSwitch = async (orgId: string) => {
    setOpen(false);
    if (orgId === activeOrgId) return;
    await switchOrg(orgId);
    // The current route may reference a project from the old org. Send the
    // user to /projects where they'll see the new org's project list.
    navigate('/projects');
  };

  const handleSettings = () => {
    setOpen(false);
    navigate('/settings');
  };

  return (
    <div ref={containerRef} className="hidden md:block relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-muted hover:bg-line-light rounded-md text-sm text-ink-secondary hover:text-ink-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
        aria-label="Switch workspace"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Building2 className="w-3.5 h-3.5 text-ink-tertiary flex-shrink-0" />
        <span className="font-medium truncate max-w-[180px]">{activeOrg.name}</span>
        <ChevronsUpDown className="w-3 h-3 text-ink-tertiary flex-shrink-0" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 min-w-[260px] bg-white border border-line rounded-md shadow-lg py-1 z-[110] animate-fade-in"
        >
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-ink-tertiary font-semibold">
            Workspaces
          </div>
          {orgs.map((o) => {
            const isActive = o.id === activeOrg.id;
            return (
              <button
                key={o.id}
                type="button"
                role="menuitem"
                onClick={() => handleSwitch(o.id)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-surface-muted transition-colors flex items-center gap-2"
              >
                <Building2 className="w-3.5 h-3.5 text-ink-tertiary flex-shrink-0" />
                <span className="flex-1 truncate text-ink-primary font-medium">{o.name}</span>
                {isActive && <Check className="w-3.5 h-3.5 text-brand-orange flex-shrink-0" />}
              </button>
            );
          })}
          <div className="border-t border-line-light mt-1 pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleSettings}
              className="w-full text-left px-3 py-2 text-sm hover:bg-surface-muted transition-colors flex items-center gap-2 text-ink-secondary"
            >
              <SettingsIcon className="w-3.5 h-3.5 text-ink-tertiary flex-shrink-0" />
              <span>Workspace settings</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
